import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, FormControl, IconButton, MenuItem, Select, TextField } from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ClearOutlined from "@mui/icons-material/ClearOutlined";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { ReactComponent as CalenderSvg } from "../../../assets/Icons/calendar.svg";
import {
  DEFAULT_HISTORY_DATE_FILTER_FIELD,
  HISTORY_DATE_FILTER_OPTIONS,
  canApplyHistoryDateRange,
  clampCreatedAtCalendarDay,
  formatHistoryDateRangeLabel,
  isPastOnlyDateFilterField,
  resolveHistoryDateRange,
  toCalendarDay,
  todayCalendarDay,
} from "./promotionHistoryColumns";

const pickerPopperProps = {
  sx: { zIndex: 1400 },
};

const isPickerPortalEvent = (event) =>
  Boolean(
    event.target.closest?.(
      ".MuiPickersPopper-root, .MuiPopover-root, .MuiMenu-paper, .MuiModal-root"
    )
  );

const toPickerValue = (value) => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const toDayString = (value) => {
  if (!value || !value.isValid?.()) return null;
  return value.format("YYYY-MM-DD");
};

export const PromotionHistoryDateFilter = ({
  field: appliedField,
  startDate: appliedStart = null,
  endDate: appliedEnd = null,
  onApply,
  onReset,
  options = HISTORY_DATE_FILTER_OPTIONS,
  defaultField = DEFAULT_HISTORY_DATE_FILTER_FIELD,
}) => {
  const rootRef = useRef(null);
  const resolvedAppliedField = appliedField || defaultField;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => ({
    field: resolvedAppliedField,
    startDate: appliedStart,
    endDate: appliedEnd,
  }));
  const today = todayCalendarDay();
  const isPastOnly = isPastOnlyDateFilterField(draft.field);
  const maxDate = isPastOnly ? dayjs(today) : undefined;
  const startValue = toPickerValue(draft.startDate);
  const endMinDate = startValue || undefined;
  const hasAppliedRange = Boolean(appliedStart && appliedEnd);
  const canApply = canApplyHistoryDateRange(draft, today);

  const emptyDraft = useCallback(
    () => ({
      field: defaultField,
      startDate: null,
      endDate: null,
    }),
    [defaultField]
  );

  const closePanel = useCallback(() => setOpen(false), []);

  const toggleOpen = () => {
    setOpen((current) => {
      if (!current) {
        setDraft({
          field: resolvedAppliedField,
          startDate: appliedStart,
          endDate: appliedEnd,
        });
      }
      return !current;
    });
  };

  const updateDraft = (next) => {
    setDraft((prev) => {
      let field = next.field ?? prev.field;
      let startDate =
        next.startDate === undefined ? prev.startDate : next.startDate;
      let endDate = next.endDate === undefined ? prev.endDate : next.endDate;

      if (isPastOnlyDateFilterField(field)) {
        startDate =
          clampCreatedAtCalendarDay(toCalendarDay(startDate), today) || null;
        endDate =
          clampCreatedAtCalendarDay(toCalendarDay(endDate), today) || null;
      }

      if (startDate && endDate && endDate < startDate) {
        endDate = null;
      }

      return { field, startDate, endDate };
    });
  };

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      if (isPickerPortalEvent(event)) return;
      closePanel();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, closePanel]);

  const handleApply = () => {
    const resolved = resolveHistoryDateRange({
      startDate: draft.startDate,
      endDate: draft.endDate,
      today,
    });
    if (!resolved) return;
    let { startDate, endDate } = resolved;
    if (isPastOnlyDateFilterField(draft.field)) {
      startDate = clampCreatedAtCalendarDay(startDate, today);
      endDate = clampCreatedAtCalendarDay(endDate, today);
    }
    if (!startDate || !endDate || endDate < startDate) return;
    onApply({
      field: draft.field,
      startDate,
      endDate,
    });
    closePanel();
  };

  const handleReset = () => {
    setDraft(emptyDraft());
    onReset();
  };

  const handleClear = (event) => {
    event.stopPropagation();
    setDraft(emptyDraft());
    onReset();
    closePanel();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div
        ref={rootRef}
        className={`promotions-history-date-filter${
          open ? " promotions-history-date-filter--open" : ""
        }`}
      >
        <button
          type="button"
          className={`promotions-history-date-filter__trigger${
            hasAppliedRange
              ? " promotions-history-date-filter__trigger--selected"
              : ""
          }`}
          aria-expanded={open}
          onClick={toggleOpen}
        >
          <CalenderSvg
            className="promotions-history-date-filter__calendar"
            aria-hidden="true"
          />
          <span className="promotions-history-date-filter__range-label">
            {hasAppliedRange
              ? formatHistoryDateRangeLabel(appliedStart, appliedEnd)
              : "Custom range"}
          </span>
          <span className="promotions-history-date-filter__trigger-actions">
            {hasAppliedRange ? (
              <IconButton
                aria-label="Clear date filter"
                size="small"
                className="promotions-history-date-filter__clear"
                onClick={handleClear}
              >
                <ClearOutlined fontSize="small" />
              </IconButton>
            ) : null}
            <ArrowForwardIosIcon className="promotions-history-date-filter__chevron" />
          </span>
        </button>
        {open ? (
          <div className="promotions-history-date-filter__panel">
            <div className="promotions-history-date-filter__col">
              <span className="promotions-history-date-filter__heading">
                Filter by
              </span>
              <FormControl
                size="small"
                className="promotions-history-date-filter__control promotions-history-date-filter__field"
              >
                <Select
                  value={draft.field}
                  onChange={(event) => updateDraft({ field: event.target.value })}
                  IconComponent={KeyboardArrowDownIcon}
                >
                  {options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="promotions-history-date-filter__row">
              <div className="promotions-history-date-filter__col">
                <span className="promotions-history-date-filter__label">
                  Start date
                </span>
                <DatePicker
                  value={startValue}
                  onChange={(value) =>
                    updateDraft({ startDate: toDayString(value) })
                  }
                  maxDate={maxDate}
                  disableFuture={isPastOnly}
                  inputFormat="DD MMM YY"
                  PopperProps={pickerPopperProps}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      className="promotions-history-date-filter__control promotions-history-date-filter__date"
                      inputProps={{
                        ...params.inputProps,
                        placeholder: "Start Date",
                      }}
                    />
                  )}
                />
              </div>
              <div className="promotions-history-date-filter__col">
                <span className="promotions-history-date-filter__label">
                  End date
                </span>
                <DatePicker
                  value={toPickerValue(draft.endDate)}
                  onChange={(value) =>
                    updateDraft({ endDate: toDayString(value) })
                  }
                  minDate={endMinDate}
                  maxDate={maxDate}
                  disableFuture={isPastOnly}
                  inputFormat="DD MMM YY"
                  PopperProps={pickerPopperProps}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      className="promotions-history-date-filter__control promotions-history-date-filter__date"
                      inputProps={{
                        ...params.inputProps,
                        placeholder: "End Date",
                      }}
                    />
                  )}
                />
              </div>
            </div>
            <div className="promotions-history-date-filter__actions">
              <Button
                variant="outlined"
                className="promotions-history-date-filter__reset"
                onClick={handleReset}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                className="promotions-history-date-filter__apply"
                disabled={!canApply}
                onClick={handleApply}
              >
                Apply
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </LocalizationProvider>
  );
};
